import React from 'react';
import { motion } from 'framer-motion';
import { EyeIcon } from '@heroicons/react/24/outline';
import { Task } from '../../types';

interface MyTasksProps {
  tasks: Task[];
  onTasksChange: (tasks: Task[]) => void;
  onTaskClick?: (task: Task) => void;
}

const MyTasks: React.FC<MyTasksProps> = ({ tasks, onTaskClick }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'TODO':
        return 'bg-gray-100 text-gray-800';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800';
      case 'REVIEW':
        return 'bg-yellow-100 text-yellow-800';
      case 'DONE':
        return 'bg-green-100 text-green-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'LOW':
        return 'bg-green-100 text-green-800';
      case 'NORMAL':
        return 'bg-blue-100 text-blue-800';
      case 'HIGH':
        return 'bg-yellow-100 text-yellow-800';
      case 'URGENT':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">My Tasks</h2>
          <p className="text-sm text-gray-500 mt-1">Click on any task card to view details and manage progress</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tasks.map((task, index) => (
          <motion.div
            key={task.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card hover:shadow-lg hover:bg-blue-50 hover:border-blue-200 cursor-pointer transition-all duration-200 group"
            onClick={() => onTaskClick?.(task)}
            title="Click to view task details"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 truncate group-hover:text-blue-700">
                  {task.title}
                </h3>
                <EyeIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </div>
              <div className="flex space-x-2 ml-2">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(task.priority)}`}>
                  {task.priority}
                </span>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(task.status)}`}>
                  {task.status.replace('_', ' ')}
                </span>
              </div>
            </div>

            {task.description && (
              <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                {task.description}
              </p>
            )}

            {task.team && (
              <div className="flex items-center space-x-2 mb-3">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: task.team.color }}
                />
                <span className="text-sm text-gray-700">{task.team.name}</span>
              </div>
            )}

            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>
                Created by {task.creator.firstName} {task.creator.lastName}
              </span>
              {task.dueDate && (
                <span>
                  Due: {new Date(task.dueDate).toLocaleDateString()}
                </span>
              )}
            </div>

            {task.assignees.length > 0 && (
              <div className="mt-3 flex items-center space-x-2">
                <span className="text-xs text-gray-500">Assignees:</span>
                <div className="flex -space-x-1">
                  {task.assignees.slice(0, 3).map((assignee) => (
                    <div
                      key={assignee.id}
                      className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center border-2 border-white"
                      title={`${assignee.user.firstName} ${assignee.user.lastName}`}
                    >
                      <span className="text-xs font-medium text-white">
                        {assignee.user.firstName[0]}{assignee.user.lastName[0]}
                      </span>
                    </div>
                  ))}
                  {task.assignees.length > 3 && (
                    <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center border-2 border-white">
                      <span className="text-xs font-medium text-gray-600">
                        +{task.assignees.length - 3}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {tasks.length === 0 && (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No tasks assigned</h3>
          <p className="mt-1 text-sm text-gray-500">
            You don't have any tasks assigned to you yet.
          </p>
        </div>
      )}
    </div>
  );
};

export default MyTasks;
